import {
    CalendarDate,
    CurrencyInCents,
    genEntityId,
    PlacementId,
    stringToCurrencyInCents,
} from "@sp-crm/core";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { fancyAlert } from "components/ui/fancy-alert";
import { InlineBanner } from "components/ui/inline-banner";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { Formik, FormikHelpers } from "formik";
import React, { MutableRefObject, Reducer, useReducer, useState } from "react";
import { handleEvent } from "util/user-events";
import {
    AdvancedSearchEntityType,
    CreateInvoiceMutationVariables,
    InvoiceLineItemInput,
    InvoicePaymentScheduleInput,
    InvoicePaymentScheduleItemStatus,
    useCreateInvoiceMutation,
} from "../../generated/graphql";
import { InvoiceInput } from "../clients/invoices";
import { MessageComposeInline } from "../messages/message-compose-internal";
import { PlacementMessageCompose } from "../messages/types";
import { PrimaryButton } from "../ui/primary-button";
import { SecondaryButton } from "../ui/secondary-button";
import { Spinner } from "../ui/spinner";
import { ContractPrefillBanner } from "./contract-prefill-banner";
import { InvoiceDetailsEditor } from "./invoice-details-editor";
import { validatePaymentSchedule } from "./invoice-helpers";
import { InvoicePaymentScheduleErrors } from "./invoice-payment-schedule-errors";

interface Props {
    clientId: PlacementId;
    invoiceInput: InvoiceInput;
    refetch: () => void;
    defaultDueDate: CalendarDate | null;
    defaultNotes: string | null;
}

const dismiss = () => 0;

interface ComponentState {
    state: "closed" | "open" | "submitting";
    errorMessage: string | null;
    lineItems: InvoiceLineItemInput[];
    paymentSchedule: InvoicePaymentScheduleInput | null;
}
type Action =
    | { type: "open" }
    | { type: "close" }
    | { type: "submit" }
    | { type: "submitFailure"; error: string | null }
    | { type: "submitSuccess" }
    | { type: "setLineItems"; lineItems: InvoiceLineItemInput[] }
    | { type: "setPaymentSchedule"; paymentSchedule: InvoicePaymentScheduleInput | null };

const initialState: ComponentState = {
    state: "closed",
    errorMessage: null,
    lineItems: [],
    paymentSchedule: null,
};
const reducer: Reducer<ComponentState, Action> = (p, a) => {
    switch (a.type) {
        case "close":
            return {
                ...p,
                ...initialState,
            };
        case "open":
            return {
                ...p,
                state: "open",
                lineItems: [
                    {
                        description: "",
                        quantity: 1,
                        id: genEntityId(),
                        unitPriceInCents: 0 as CurrencyInCents,
                    },
                ],
            };
        case "submit":
            return { ...p, state: "submitting" };
        case "submitSuccess":
            return {
                ...p,
                ...initialState,
            };
        case "submitFailure":
            return { ...p, state: "open", errorMessage: a.error };
        case "setLineItems":
            return { ...p, lineItems: a.lineItems };
        case "setPaymentSchedule":
            return { ...p, paymentSchedule: a.paymentSchedule };
    }
};

interface FormValues {
    serial: string;
    amount: string;
    status: string;
    sentDate: string | undefined;
    dueDate: string | undefined;
    generatePdf: "noop" | "email" | "pdf";
    notes: string;
    externalReference: string;
}

const generateInitialValues = (
    defaultDueDate: CalendarDate | null,
    defaultNotes: string | null,
): FormValues => {
    return {
        serial: "",
        amount: "",
        status: "sent",
        sentDate: CalendarDate.todayNoTime.toString(),
        dueDate: defaultDueDate?.toString(),
        generatePdf: "noop",
        notes: defaultNotes ?? "",
        externalReference: "",
    };
};

export const InvoiceCreateInternal = (props: Props) => {
    const { clientId, refetch, invoiceInput, defaultDueDate, defaultNotes } = props;
    const placementIdStr = clientId;
    const [{ state, errorMessage, lineItems, paymentSchedule }, dispatch] = useReducer(
        reducer,
        initialState,
    );
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const [contractInfo, setContractInfo] = useState<{
        rateLabel: string;
        requiresResidentCharges: boolean;
    } | null>(null);
    const [residentCharges, setResidentCharges] = useState("");
    const [prefilledFromContract, setPrefilledFromContract] = useState(false);

    const fetchPrefill = (baseAmountCents: number) =>
        queryClient.fetchQuery(
            trpcClient.invoiceGeneration.prefillFromContract.queryOptions({
                clientId,
                baseAmountCents,
            }),
        );

    const closeAction = () => {
        setContractInfo(null);
        setResidentCharges("");
        setPrefilledFromContract(false);
        dispatch({ type: "close" });
    };

    const applyPrefill = (
        prefill: Awaited<ReturnType<typeof fetchPrefill>>,
        trigger: "open" | "recalculate",
    ) => {
        if (!prefill.prefilled || prefill.lineItems.length === 0) {
            return;
        }
        handleEvent("invoice-contract-prefill", {
            source: "placement-invoice",
            trigger,
            multiPayment: (prefill.paymentSchedule?.length ?? 0) > 1,
        });
        dispatch({
            type: "setLineItems",
            lineItems: prefill.lineItems.map(lineItem => ({
                id: genEntityId(),
                description: lineItem.description,
                quantity: 1,
                unitPriceInCents: lineItem.amountCents as CurrencyInCents,
            })),
        });
        dispatch({
            type: "setPaymentSchedule",
            paymentSchedule: prefill.paymentSchedule
                ? {
                      items: prefill.paymentSchedule.map(item => ({
                          id: genEntityId(),
                          status: InvoicePaymentScheduleItemStatus.Due,
                          paidDate: null as string | null,
                          dueDate: item.dueDate,
                          amountInCents: item.amountInCents as CurrencyInCents,
                      })),
                  }
                : null,
        });
        setPrefilledFromContract(true);
    };

    const openAction = async () => {
        setContractInfo(null);
        setResidentCharges("");
        setPrefilledFromContract(false);
        dispatch({ type: "open" });
        const prefill = await fetchPrefill(0);
        if (!prefill.prefilled) {
            return;
        }
        setContractInfo({
            rateLabel: prefill.rateLabel,
            requiresResidentCharges: prefill.requiresResidentCharges,
        });
        applyPrefill(prefill, "open");
    };

    const recalcFromResidentCharges = async () => {
        const baseAmountCents = Math.round((parseFloat(residentCharges) || 0) * 100);
        applyPrefill(await fetchPrefill(baseAmountCents), "recalculate");
    };
    const mutation = useCreateInvoiceMutation();
    const inlineEditorRef =
        React.useRef<PlacementMessageCompose>() as MutableRefObject<PlacementMessageCompose>;

    const submitForm = React.useCallback(
        (values: FormValues, helpers: FormikHelpers<FormValues>) => {
            const message =
                values.generatePdf === "email" && inlineEditorRef.current
                    ? inlineEditorRef.current.getMessage()
                    : null;
            const payload: CreateInvoiceMutationVariables = {
                clientId: placementIdStr,
                amountCents: stringToCurrencyInCents(values.amount),
                serial: values.serial === "" ? null : values.serial,
                status: values.status,
                sentDate: values.sentDate === "" ? null : values.sentDate,
                dueDate: values.dueDate === "" ? null : values.dueDate,
                generatePdf:
                    values.generatePdf === "email" || values.generatePdf === "pdf",
                notes: values.notes === "" ? null : values.notes,
                message,
                lineItems: lineItems,
                paymentSchedule: paymentSchedule,
                externalReference: values.externalReference,
            };

            (async () => {
                if (message && (!message.recipients || message.recipients.length === 0)) {
                    await fancyAlert(
                        "Missing Recipients",
                        "At least one recipient is required to send an invoice. Please add a recipient and try again.",
                    );
                    return;
                }
                helpers.setSubmitting(true);
                dispatch({ type: "submit" });
                const result = await mutation.mutateAsync(payload);
                helpers.setSubmitting(false);
                if (result.createInvoice.errors) {
                    dispatch({
                        type: "submitFailure",
                        error:
                            result.createInvoice.errors.message ??
                            "Sorry, there were errors.",
                    });
                    result.createInvoice.errors.fields.forEach(e => {
                        helpers.setFieldError(
                            e.name === "amountCents" ? "amount" : e.name,
                            e.message,
                        );
                    });
                } else {
                    helpers.resetForm();
                    dispatch({ type: "submitSuccess" });
                    refetch();
                }
            })();
        },
        [dispatch, mutation, lineItems, paymentSchedule, placementIdStr, refetch],
    );

    const clientLinkedCorrectly = !!invoiceInput.communityName;

    const paymentScheduleValidationErrors = validatePaymentSchedule(
        lineItems,
        paymentSchedule,
    );

    return (
        <div>
            <div className="space-y-4">
                {clientLinkedCorrectly ? null : (
                    <InlineBanner type="warning">
                        This client is not yet associated with a community. Set the{" "}
                        <em>community</em> to ensure the invoice is correctly associated
                        with the community.
                    </InlineBanner>
                )}
                <PrimaryButton
                    onClick={e => {
                        e.preventDefault();
                        void openAction();
                    }}>
                    Add Invoice
                </PrimaryButton>
            </div>
            <ResponsivePanel
                headerText="Create Invoice"
                isOpen={state === "open" || state === "submitting"}
                onDismiss={closeAction}
                type={PanelType.extraLarge}>
                <div>
                    <Formik
                        onSubmit={submitForm}
                        initialValues={generateInitialValues(
                            defaultDueDate,
                            defaultNotes,
                        )}>
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            touched,
                            validateForm,
                            setFieldValue,
                            setFieldTouched,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <KludgeForceValidate
                                    validateForm={validateForm}
                                    invoiceInput={invoiceInput}
                                />
                                <div className="space-y-6 border-b pb-8 border-solid text-base">
                                    {errorMessage ? (
                                        <div className="bg-red-200 text-red-700 p-4 rounded font-bold mb-2">
                                            {errorMessage}
                                        </div>
                                    ) : null}
                                    {contractInfo ? (
                                        <ContractPrefillBanner
                                            communityName={
                                                invoiceInput.communityName ?? null
                                            }
                                            rateLabel={contractInfo.rateLabel}
                                            requiresResidentCharges={
                                                contractInfo.requiresResidentCharges
                                            }
                                            residentCharges={residentCharges}
                                            onResidentChargesChange={setResidentCharges}
                                            onApply={() =>
                                                void recalcFromResidentCharges()
                                            }
                                            applied={prefilledFromContract}
                                            hint={
                                                prefilledFromContract
                                                    ? "Amounts were prefilled from the contract terms — review the line items and payment schedule below."
                                                    : "Enter the total resident charges, then apply to set the payment amounts."
                                            }
                                        />
                                    ) : null}
                                    <div className="text-xl font-semibold">
                                        Invoice Type
                                    </div>
                                    <div className="">
                                        <div className="">
                                            <label>
                                                <input
                                                    type="radio"
                                                    className="form-radio"
                                                    name="generatePdf"
                                                    value="noop"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    checked={
                                                        values.generatePdf === "noop"
                                                    }
                                                />
                                                <span className="ml-2">
                                                    Log invoice record only (don&apos;t
                                                    generate a PDF invoice)
                                                </span>
                                            </label>
                                        </div>
                                        <div className="">
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="generatePdf"
                                                    value="email"
                                                    className="form-radio"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    checked={
                                                        values.generatePdf === "email"
                                                    }
                                                />
                                                <span className="ml-2">
                                                    Email PDF invoice
                                                </span>
                                            </label>
                                        </div>
                                        <div className="">
                                            <label>
                                                <input
                                                    type="radio"
                                                    name="generatePdf"
                                                    value="pdf"
                                                    className="form-radio"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    checked={values.generatePdf === "pdf"}
                                                />
                                                <span className="ml-2">
                                                    Generate a new PDF invoice
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                    <InvoiceDetailsEditor
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        setFieldValue={setFieldValue}
                                        setFieldTouched={setFieldTouched}
                                        values={values}
                                        touched={touched}
                                        errors={errors}
                                        lineItems={lineItems}
                                        onLineItemsChange={lineItems =>
                                            dispatch({ type: "setLineItems", lineItems })
                                        }
                                        paymentSchedule={paymentSchedule}
                                        onPaymentScheduleChange={paymentSchedule => {
                                            dispatch({
                                                type: "setPaymentSchedule",
                                                paymentSchedule,
                                            });
                                        }}
                                    />
                                    {values.generatePdf === "email" ? (
                                        <div>
                                            <MessageComposeInline
                                                entityId={clientId}
                                                entityType={
                                                    AdvancedSearchEntityType.Client
                                                }
                                                onDismiss={dismiss}
                                                requestSpecialMessageExperience="invoice"
                                                ref={inlineEditorRef}
                                            />
                                        </div>
                                    ) : null}
                                    <InvoicePaymentScheduleErrors
                                        errors={paymentScheduleValidationErrors}
                                    />
                                    <div className="flex space-x-4 my-4">
                                        <PrimaryButton
                                            type="submit"
                                            disabled={
                                                state === "submitting" ||
                                                paymentScheduleValidationErrors.length > 0
                                            }>
                                            {state === "submitting" ? (
                                                <Spinner />
                                            ) : values.generatePdf === "email" ? (
                                                "Send"
                                            ) : (
                                                "Save"
                                            )}
                                        </PrimaryButton>
                                        <SecondaryButton
                                            onClick={e => {
                                                e.preventDefault();
                                                closeAction();
                                            }}>
                                            Cancel
                                        </SecondaryButton>
                                    </div>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            </ResponsivePanel>
        </div>
    );
};

interface KludgeForceValidateProps {
    validateForm: () => void;
    invoiceInput: InvoiceInput;
}

const KludgeForceValidate: React.FC<KludgeForceValidateProps> = props => {
    React.useEffect(() => {
        props.validateForm();
    }, [props.invoiceInput]); // eslint-disable-line react-hooks/exhaustive-deps
    return null;
};
